import React, { type FunctionComponent, useEffect, useState } from 'react'
import { css } from '@emotion/react'
import {
  Button,
  colors,
  fontSize,
  LoadingDots,
  spacing,
  SimpleSelect,
} from '@retailer-platform/shared-components'
import { type SimpleOption } from '@retailer-platform/shared-components/src/common/select/utils/select.types'
import { usePartnerPickDevicesForm } from 'domains/partner-pick-devices/components/form/utils/usePartnerPickDevicesForm.hooks'
import {
  PartnerPickDeviceAccountTypeEnum,
  type PartnerPickDeviceDatum,
  type PartnerPickDevicesFormData,
} from 'domains/partner-pick-devices/types/partnerPickDevices.types'
import { PartnerPickDevicesFormInput } from 'domains/partner-pick-devices/components/form/components/input/PartnerPickDevicesFormInput'
import { PartnerPickDevicesFormPassword } from 'domains/partner-pick-devices/components/form/components/password/PartnerPickDevicesFormPassword'
import { PartnerPickDevicesFormRadioGroup } from 'domains/partner-pick-devices/components/form/components/radio-group/PartnerPickDevicesFormRadioGroup'
import { PartnerPickDevicesFormRadio } from 'domains/partner-pick-devices/components/form/components/radio/PartnerPickDevicesFormRadio'
import {
  type DomainMessages,
  useDomainMessages,
} from 'domains/partner-pick-devices/utils/domain/intl'
import { DeviceTypeEnum } from 'domains/partner-pick-devices/api'
import { emailValidationPattern } from 'utils/validators/emailValidation'
import { phoneNumberValidationPattern } from 'utils/validators/phoneNumberValidation'
import { type WarehouseLocation } from 'legacy/common/types'

interface PartnerPickDevicesFormProps {
  formData: PartnerPickDevicesFormData
  warehouseLocationId: string
  deviceId?: PartnerPickDeviceDatum['id']
  onSave: (savedDevice: PartnerPickDeviceDatum) => void
  onCancel: () => void
  warehouseLocations: WarehouseLocation[]
}

const formTitleStyles = css({
  fontSize: fontSize.X18,
  marginBottom: spacing.X36,
  fontWeight: 'bold',
  textAlign: 'center',
})

const nameContainerStyles = css({
  display: 'flex',
})

const firstNameFieldStyles = css({
  flex: '1',
  marginRight: spacing.X16,
})

const lastNameFieldStyles = css({
  flex: '1',
})

const errorMessageStyles = css({
  color: colors.DETRIMENTAL.REGULAR,
  marginBottom: spacing.X12,
})

const footerButtonStyles = css({
  display: 'flex',
  justifyContent: 'end',
  marginRight: `-${spacing.X8}`,
})

const submitButtonStyles = css({
  display: 'inline-flex',
  '& span + *': {
    // space out the loading indicator from button text
    marginLeft: spacing.X12,
  },
})

export const PartnerPickDevicesForm: FunctionComponent<PartnerPickDevicesFormProps> = ({
  formData,
  warehouseLocationId: warehouseLocationIdForDevicePage,
  deviceId,
  onSave,
  onCancel,
  warehouseLocations,
}) => {
  const [warehouseLocationIdForAccount, setWarehouseLocationIdForAccount] = useState(
    () => warehouseLocationIdForDevicePage
  )
  const {
    register,
    handleSubmit,
    errors,
    deviceType,
    setDeviceType,
    accountType,
    setAccountType,
    submitForm,
    formSubmitData,
    formSubmitSuccess,
    formSubmitLoading,
    formSubmitError,
  } = usePartnerPickDevicesForm({
    initialFormData: formData,
    deviceId,
    warehouseLocationIdForDevicePage,
    warehouseLocationIdForAccount,
  })

  useEffect(() => {
    if (formSubmitSuccess) {
      onSave(formSubmitData)
    }
  }, [formSubmitData, formSubmitSuccess, onSave])

  const messages = useDomainMessages({
    add: 'partnerPickDevicesDomain.modal.title.add',
    edit: 'partnerPickDevicesDomain.modal.title.edit',
    cancel: 'partnerPickDevicesDomain.modal.cancel',
    save: 'partnerPickDevicesDomain.modal.save',
    error: 'partnerPickDevicesDomain.error',
  })

  const defaultErrorMessageIds: Record<string, DomainMessages> = {
    required: 'partnerPickDevicesDomain.validation.required',
  }
  const isEditMode = !!deviceId
  const warehouseLocationOptions = (warehouseLocations?.map(({ id, name }) => ({
    label: name,
    value: id,
  })) ?? []) as SimpleOption<string>[]

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <div css={formTitleStyles}>{isEditMode ? messages.edit : messages.add}</div>

      <PartnerPickDevicesFormRadioGroup
        labelMessageId="partnerPickDevicesDomain.form.deviceType.label"
        tooltipMessageId="partnerPickDevicesDomain.form.deviceType.tooltip"
      >
        <PartnerPickDevicesFormRadio
          identifier="deviceType"
          labelMessageId="partnerPickDevicesDomain.details.deviceType.phone"
          value={DeviceTypeEnum.Phone}
          selectedValue={deviceType}
          setValue={setDeviceType}
          disabled={formSubmitLoading}
        />
        <PartnerPickDevicesFormRadio
          identifier="deviceType"
          labelMessageId="partnerPickDevicesDomain.details.deviceType.wifi"
          value={DeviceTypeEnum.Wifi}
          selectedValue={deviceType}
          setValue={setDeviceType}
          disabled={formSubmitLoading}
        />
      </PartnerPickDevicesFormRadioGroup>

      <PartnerPickDevicesFormRadioGroup
        labelMessageId="partnerPickDevicesDomain.form.accountType.label"
        tooltipMessageId="partnerPickDevicesDomain.form.accountType.tooltip"
      >
        <PartnerPickDevicesFormRadio
          identifier="accountType"
          labelMessageId="partnerPickDevicesDomain.details.accountType.shopping"
          value={PartnerPickDeviceAccountTypeEnum.Shopping}
          selectedValue={accountType}
          setValue={setAccountType}
          disabled={formSubmitLoading}
        />
        <PartnerPickDevicesFormRadio
          identifier="accountType"
          labelMessageId="partnerPickDevicesDomain.details.accountType.manager"
          value={PartnerPickDeviceAccountTypeEnum.Manager}
          selectedValue={accountType}
          setValue={setAccountType}
          disabled={formSubmitLoading}
        />
      </PartnerPickDevicesFormRadioGroup>

      <div css={nameContainerStyles}>
        <PartnerPickDevicesFormInput
          identifier="firstName"
          ref={register({ required: true })}
          labelMessageId="partnerPickDevicesDomain.form.firstName.label"
          errors={errors}
          errorMessageIds={defaultErrorMessageIds}
          styles={firstNameFieldStyles}
          disabled={formSubmitLoading}
        />

        <PartnerPickDevicesFormInput
          identifier="lastName"
          ref={register({ required: true })}
          labelMessageId="partnerPickDevicesDomain.form.lastName.label"
          errors={errors}
          errorMessageIds={defaultErrorMessageIds}
          styles={lastNameFieldStyles}
          disabled={formSubmitLoading}
        />
      </div>

      <PartnerPickDevicesFormInput
        identifier="phone"
        ref={register({
          required: deviceType === DeviceTypeEnum.Phone,
          pattern: deviceType === DeviceTypeEnum.Phone ? phoneNumberValidationPattern : null,
        })}
        labelMessageId={
          deviceType === DeviceTypeEnum.Phone
            ? 'partnerPickDevicesDomain.form.phone.label'
            : 'partnerPickDevicesDomain.form.phone.wifi.label'
        }
        placeholderMessageId={
          deviceType === DeviceTypeEnum.Phone
            ? 'partnerPickDevicesDomain.form.phone.label'
            : 'partnerPickDevicesDomain.form.phone.wifi.placeholder'
        }
        errors={errors}
        errorMessageIds={{
          ...defaultErrorMessageIds,
          pattern: 'partnerPickDevicesDomain.validation.phone',
        }}
        disabled={deviceType === DeviceTypeEnum.Wifi || formSubmitLoading}
      />

      <PartnerPickDevicesFormInput
        identifier="email"
        ref={register({ required: true, pattern: emailValidationPattern })}
        labelMessageId="partnerPickDevicesDomain.form.email.label"
        errors={errors}
        errorMessageIds={{
          ...defaultErrorMessageIds,
          pattern: 'partnerPickDevicesDomain.validation.email',
        }}
        type="email"
        disabled={formSubmitLoading}
      />

      <PartnerPickDevicesFormPassword
        identifier="password"
        ref={register({ required: deviceType === DeviceTypeEnum.Wifi && !deviceId })}
        labelMessageId="partnerPickDevicesDomain.form.password.label"
        errors={errors}
        errorMessageIds={defaultErrorMessageIds}
        disabled={formSubmitLoading}
      />

      {isEditMode && (
        // enable moving devices from store to store
        // https://instacart.atlassian.net/browse/FUL-64566
        <SimpleSelect
          onChange={(whlId: string) => setWarehouseLocationIdForAccount(whlId)}
          value={warehouseLocationIdForAccount}
          options={warehouseLocationOptions}
        />
      )}

      {formSubmitError && (
        <div css={errorMessageStyles} data-testid="partnerPickDevicesForm-apiError">
          {messages.error}
        </div>
      )}

      <div css={footerButtonStyles}>
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={formSubmitLoading}
          data-testid="partnerPickDevicesForm-cancel"
        >
          {messages.cancel}
        </Button>
        <Button
          type="submit"
          disabled={formSubmitLoading}
          data-testid="partnerPickDevicesForm-submit"
        >
          <div css={submitButtonStyles}>
            <span>{messages.save}</span>

            {formSubmitLoading && <LoadingDots height={10} />}
          </div>
        </Button>
      </div>
    </form>
  )
}
